import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Close } from '@videoblocks/react-icons';

import {
  selectAuthPermissions,
  selectHasAnySubscription,
  selectUseSnapshotsUI,
  selectEnableSearchHistory,
} from '../../auth/AuthSelectors';
import { searchOrigins } from '../Navigation/constants';
import { isSearchAppLoaded } from '../Search/actions/SearchActions';
import { saveSearchHistory } from '../Search/components/navSearch/AutosuggestElements/util';
import { NavSearchContextProvider } from '../Search/components/navSearch/NavSearchContext';
import CrossAssetNavSearchBox from '../Search/components/navSearch/NavSearchElements/CrossAssetNavSearchBox';
import {
  cleanSearchKeywords,
  SearchBarVariant,
} from '../Search/components/navSearch/NavSearchElements/searchUtils';
import ProminentSearchDropdown from '../Search/components/navSearch/ProminentSearchElements/ProminentSearchDropdown';
import { handleSubmit as handleSearchSubmit } from '../Search/containers/NavSearchContainer';
import {
  selectIsEnterpriseMember,
  selectSearchFilterOptions,
} from '../Search/selectors/searchSelectors';
import { fetchTypeahead } from '../Search/utils/searchUtils';
import { SearchFilterContentTypes } from '../Shared/enums';

interface TopNavSearchBarProps {
  onCollapse: () => void;
  showXButton: boolean;
}

export default function TopNavSearchBar({
  onCollapse,
  showXButton,
}: TopNavSearchBarProps): JSX.Element {
  const dispatch = useDispatch();

  const selectedSearchFilterOptions = useSelector(selectSearchFilterOptions);
  const userHasAnySubscription = useSelector(selectHasAnySubscription);
  const isEnterpriseMember = useSelector(selectIsEnterpriseMember);
  const { pproPluginEnabled } = useSelector(selectAuthPermissions);
  const useSnapshotsUI = useSelector(selectUseSnapshotsUI);
  const enableSearchHistory = useSelector(selectEnableSearchHistory);

  const { contentType: reduxContentType = '' } = selectedSearchFilterOptions;

  const [contentType, setContentType] = useState<SearchFilterContentTypes>(
    reduxContentType as SearchFilterContentTypes
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState({
    keywords: [],
    relatedCollections: [],
    latestCollections: [],
    trendingCollections: [],
    relatedContentTypesAndKeywords: [],
  });

  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    setContentType(reduxContentType as SearchFilterContentTypes);
  }, [reduxContentType]);

  const clearSuggestions = useCallback(
    () =>
      setSuggestions({
        keywords: [],
        relatedCollections: [],
        latestCollections: [],
        trendingCollections: [],
        relatedContentTypesAndKeywords: [],
      }),
    []
  );

  const handleDropdownChange = (value: string) => {
    setContentType(value as SearchFilterContentTypes);
  };

  const handleSubmit = (
    contentTypeSearched = contentType,
    value = searchTerm,
    searchOriginOverride = ''
  ) => {
    const cleanedSearchKeywords = cleanSearchKeywords(value);
    const searchOrigin = searchOriginOverride || searchOrigins.TOP_NAV;
    if (enableSearchHistory) {
      saveSearchHistory(contentTypeSearched, cleanedSearchKeywords);
    }
    handleSearchSubmit(
      contentTypeSearched,
      cleanedSearchKeywords,
      searchOrigin,
      dispatch,
      selectedSearchFilterOptions
    );
  };

  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleSubmit(contentType, searchTerm);
  };

  const handleOnClose = () => {
    if (searchTerm.trim()) {
      setSearchTerm('');
      clearSuggestions();
    } else {
      onCollapse();
    }
  };

  return (
    <NavSearchContextProvider
      contentType={contentType}
      onSubmit={handleSubmit}
      userHasAnySubscription={userHasAnySubscription}
      fetchTypeahead={fetchTypeahead}
      isEnterpriseMember={isEnterpriseMember}
      pproPluginEnabled={pproPluginEnabled}
      isSearchAppLoaded={isSearchAppLoaded}
      useSnapshots={useSnapshotsUI}
      enableSearchHistory={enableSearchHistory}
      searchBarVariant={SearchBarVariant.TopNav}
    >
      <form
        ref={formRef}
        className="w-full max-w-1034px mx-auto bg-white border-2 border-gray-300 rounded-full flex items-center justify-between px-2 h-12 relative good"
        onSubmit={handleFormSubmit}
        data-cy="topnav-search-bar"
      >
        <ProminentSearchDropdown
          onChange={handleDropdownChange}
          value={contentType}
          useNewSearchUI
          useSnapshots={useSnapshotsUI}
          clearSuggestions={clearSuggestions}
          size="compact"
        />
        <div className="flex-1 min-w-0">
          <CrossAssetNavSearchBox
            contentType={contentType}
            setSearchTerm={setSearchTerm}
            onSubmit={handleSubmit}
            searchTerm={searchTerm}
            suggestions={suggestions}
            setSuggestions={setSuggestions}
            clearSuggestions={clearSuggestions}
          />
        </div>
        {showXButton && (
          <button
            type="button"
            onClick={handleOnClose}
            aria-label="Clear or close search"
            className="flex-shrink-0 p-2 mr-2 text-gray-500 hover:text-gray-700"
          >
            <Close className="w-4 h-4" />
          </button>
        )}
      </form>
    </NavSearchContextProvider>
  );
}
